import { MMButton } from '@components'
import LoginPane from '@walletPayment/components/LoginPane'
import PreviewPane from '@walletPayment/components/PreviewPane'
import SelectAccountPane from '@walletPayment/components/SelectAccountPane'
import { WalletCheckoutSteps } from '@walletPayment/constants'
import type { WalletCheckoutStep } from '@walletPayment/types'
import WalletOtp from '@walletPayment/views/WalletOtp'
import { useAuthRequest, useLogoutRequest } from 'mmfintech-checkout-commons'
import { tr } from 'mmfintech-commons'
import { useEffect, useState } from 'react'
import { TailSpin } from 'react-loader-spinner'
import { shallowEqual, useSelector } from 'react-redux'

import './WalletCheckout.scss'

type WalletCheckoutProps = {
  sessionId: string
  handlePay: (accountId: number) => void
  onCancel: () => void
  hasStorageAccess: boolean
  setStorageAccess: (enable: boolean) => void
}

const WalletCheckout = (props: WalletCheckoutProps) => {
  const { sessionId, handlePay, onCancel, hasStorageAccess, setStorageAccess } = props

  const { session } = useSelector(
    ({ checkout: { session } }) => ({
      session
    }),
    shallowEqual
  )

  const [step, setStep] = useState<WalletCheckoutStep>(WalletCheckoutSteps.LOADING)
  const [selectedAccount, setSelectedAccount] = useState(null)

  const { authRequest } = useAuthRequest()
  const { logoutRequest } = useLogoutRequest()

  const handleCancel = () => {
    logoutRequest()
    onCancel()
  }

  const handleLoginResponse = (response?: any) => {
    setStep(response?.data?.challenge ? WalletCheckoutSteps.OTP : WalletCheckoutSteps.SELECT_ACCOUNT)
  }

  const requestAccess = () => {
    const noAccess = () => {
      console.error('Cannot obtain storage access')
      onCancel()
    }

    const continueWithPayment = () => {
      setStorageAccess(true)
      authRequest(
        () => setStep(WalletCheckoutSteps.SELECT_ACCOUNT),
        () => setStep(WalletCheckoutSteps.LOGIN)
      )
    }

    if (typeof document.requestStorageAccess === 'function') {
      document
        .requestStorageAccess()
        .then(
          () => continueWithPayment(),
          () => noAccess()
        )
        .catch(() => {
          noAccess()
        })
    } else {
      continueWithPayment()
    }
  }

  const openPopup = () => {
    setStep(WalletCheckoutSteps.LOADING)
    const handler = window.open(
      `${window.origin}/auth/sessionId/${sessionId}`,
      '_blank',
      'popup=1,location=0,toolbar=0,menubar=0,status=0'
    )
    if (handler) {
      const interval = setInterval(() => {
        if (handler.closed) {
          clearInterval(interval)
          //@ts-ignore
          if (handler.userLoggedIn) {
            setStep(WalletCheckoutSteps.REQUEST_ACCESS)
          } else {
            onCancel()
          }
        }
      }, 500)
    } else {
      onCancel()
    }
  }

  useEffect(() => {
    if (hasStorageAccess) {
      setStep(!session?.isSubsequent ? WalletCheckoutSteps.LOGIN : WalletCheckoutSteps.SELECT_ACCOUNT)
    } else {
      setStep(WalletCheckoutSteps.OPEN_POPUP)
    }

    return () => {
      setStep(WalletCheckoutSteps.LOADING)
    }
  }, [])

  return (
    <>
      {step === WalletCheckoutSteps.LOADING && (
        <div className='mb-4 mt-4'>
          <TailSpin width='8rem' height='8rem' color='#939ea7' />
        </div>
      )}

      {step === WalletCheckoutSteps.OPEN_POPUP && (
        <div className='wallet-checkout-wrapper' data-test='wallet-checkout-request-access'>
          <div className='heading'>{tr('CHECKOUT.WALLET_PAYMENT.OPEN_POPUP_TITLE', 'Sign in')}</div>
          <p>
            {tr(
              'CHECKOUT.WALLET_PAYMENT.OPEN_POPUP',
              'In order to continue with Jetonbank, please click the button below. A new popup window will appear.'
            )}
          </p>

          <div className='form-actions'>
            <MMButton
              type='button'
              variant='primary'
              caption={tr('CHECKOUT.BUTTONS.CONTINUE', 'Continue')}
              onClick={openPopup}
              fullWidth
            />
            <MMButton
              type='button'
              variant='secondary'
              caption={tr('CHECKOUT.BUTTONS.BACK', 'Back')}
              onClick={onCancel}
              data-test='button-back'
              fullWidth
            />
          </div>
        </div>
      )}

      {step === WalletCheckoutSteps.REQUEST_ACCESS && (
        <div className='wallet-checkout-wrapper' data-test='wallet-checkout-request-access'>
          <div className='heading'>{tr('CHECKOUT.WALLET_PAYMENT.ACCESS_REQUEST_TITLE', 'Cookie consent')}</div>
          <p>
            {tr(
              'CHECKOUT.WALLET_PAYMENT.ACCESS_REQUEST',
              'Our website use cookies to provide safe user experience. Please click the button below to accept.'
            )}
          </p>

          <div className='form-actions'>
            <MMButton
              type='button'
              variant='primary'
              caption={tr('CHECKOUT.BUTTONS.CONTINUE', 'Continue')}
              onClick={requestAccess}
              fullWidth
            />
            <MMButton
              type='button'
              variant='secondary'
              caption={tr('CHECKOUT.BUTTONS.BACK', 'Back')}
              onClick={onCancel}
              data-test='button-back'
              fullWidth
            />
          </div>
        </div>
      )}

      {step === WalletCheckoutSteps.LOGIN && (
        <LoginPane sessionId={sessionId} session={session} onSuccess={handleLoginResponse} onCancel={onCancel} />
      )}

      {step === WalletCheckoutSteps.OTP && (
        <WalletOtp onSuccess={() => setStep(WalletCheckoutSteps.SELECT_ACCOUNT)} onCancel={onCancel} />
      )}

      {step === WalletCheckoutSteps.SELECT_ACCOUNT && (
        <SelectAccountPane
          onSuccess={() => setStep(WalletCheckoutSteps.PREVIEW)}
          onCancel={handleCancel}
          selectedAccount={selectedAccount}
          setSelectedAccount={setSelectedAccount}
        />
      )}

      {step === WalletCheckoutSteps.PREVIEW && (
        <PreviewPane
          onCancel={() => setStep(WalletCheckoutSteps.SELECT_ACCOUNT)}
          selectedAccount={selectedAccount}
          handlePay={() => handlePay(selectedAccount.id)}
        />
      )}
    </>
  )
}

export default WalletCheckout
